import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaSave,
  FaTimes,
  FaImage,
  FaEnvelope,
  FaPhone,
  FaUser,
  FaUserTie,
  FaListOl,
  FaCloudUploadAlt,
  FaLink,
  FaCrop,
} from "react-icons/fa";
import Button from "../../components/Button/Button";
import { updateTeamMember } from "../../services/teamServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  uploadTeamPhoto,
  toAbsolutePhotoUrl as toAbsTeamPhoto,
} from "../../services/teamPhotoServices";
import CropPhotoModal, {
  toMasterUrl,
} from "../../components/CropPhotoModal/CropPhotoModal";
import { cropTeamPhoto } from "../../services/teamPhotoServices";

// Quill toolbar/modules (présentation seulement)
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const EditTeamMemberModal = ({
  isActive,
  onClose,
  refreshTeam,
  memberData,
}) => {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    fonction: "",
    photoURL: "",
    categorie: "",
    position: "",
    mail_member: "",
    tel_member: "",
    bio_member: "",
  });

  const defaultAvatar = "/img/user_avatar.jpg";
  const [previewSrc, setPreviewSrc] = useState(defaultAvatar);
  const dialogRef = useRef(null);

  // Upload state (même logique que la modale adhérent)
  const [uploadMode, setUploadMode] = useState("url"); // 'url' | 'upload'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // 'webp' | 'jpg' | 'png'
  const fileInputRef = useRef(null);
  const [imgVersion, setImgVersion] = useState(0); // anti-cache
  const [imgOk, setImgOk] = useState(true);

  // Crop modal
  const [isCropOpen, setIsCropOpen] = useState(false);
  

  // Hydrate form depuis memberData
  useEffect(() => {
    if (!memberData) return;
    const data = {
      nom: memberData.nom || "",
      prenom: memberData.prenom || "",
      fonction: memberData.fonction || "",
      photoURL: memberData.photoURL || "",
      categorie: memberData.categorie || "",
      position: memberData.position ?? "",
      mail_member: memberData.mail_member || "",
      tel_member: memberData.tel_member || "",
      bio_member: memberData.bio_member || "",
    };
    setFormData(data);
    const initialPreview = data.photoURL?.trim()
      ? toAbsTeamPhoto(data.photoURL)
      : defaultAvatar;
    setPreviewSrc(initialPreview);
    setUploadMode("url");
    setUploadProgress(0);
    setIsUploading(false);
    setImgVersion((v) => v + 1);
    setImgOk(true);
  }, [memberData]);

  // Ouverture : lock scroll + focus + ESC
  useEffect(() => {
    if (!isActive) return;

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handleClose = () => {
    onClose?.();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "photoURL") {
      const val = value.trim();
      setPreviewSrc(val ? toAbsTeamPhoto(val) : defaultAvatar);
      setImgVersion((v) => v + 1);
      setImgOk(true);
    }
  };

  const openFilePicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!memberData?.id) {
      toast.error("ID membre manquant.");
      return;
    }
    try {
      setIsUploading(true);
      setUploadProgress(0);
      const res = await uploadTeamPhoto(memberData.id, file, {
        format: outputFormat,
        onUploadProgress: (pe) => {
          if (!pe.total) return;
          const percent = Math.round((pe.loaded * 100) / pe.total);
          setUploadProgress(percent);
        },
      });
      // res: { id, photoURL }
      setFormData((prev) => ({ ...prev, photoURL: res.photoURL || "" }));
      setPreviewSrc(toAbsTeamPhoto(res.photoURL || ""));
      setImgOk(true);
      setImgVersion((v) => v + 1);
      toast.success("Photo uploadée et traitée (400×400).");
    } catch (err) {
      console.error(err);
      toast.error(`Erreur upload: ${err?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isUploading) {
        toast.info("Patiente, l'upload est en cours…");
        return;
      }
      const payload = {
        ...formData,
        // Ne force pas un avatar par défaut ici : conserve le champ vide si pas de photo
      };

      await updateTeamMember(memberData.id, payload);
      toast.success("Membre modifié avec succès");
      handleClose();
      refreshTeam?.();
    } catch (error) {
      console.error("Erreur lors de la modification du membre", error);
      toast.error("Erreur lors de la modification du membre");
    }
  };

  if (!isActive) return null;

  const absolutePhoto = toAbsTeamPhoto(formData.photoURL);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier le membre de l'équipe"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh] sm:max-h-[92dvh]"
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Modifier Membre
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Mettez à jour les informations du membre de l’équipe.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps + Footer dans un seul <form> */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 sm:gap-6">
            {/* Avatar + URL/Upload */}
            <div className="flex md:flex-col gap-4 md:gap-5">
              <div className="relative self-start">
                <img
                  src={`${absolutePhoto || defaultAvatar}?v=${imgVersion}`}
                  alt="Aperçu"
                  onError={(e) => {
                    e.currentTarget.src = defaultAvatar;
                    setImgOk(false);
                  }}
                  className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover shadow ring-2 ring-white dark:ring-slate-800"
                />
                <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-[12px]" />
                </span>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="photoMode"
                      value="url"
                      checked={uploadMode === "url"}
                      onChange={() => setUploadMode("url")}
                    />
                    <span className="flex items-center gap-2 text-sm">
                      <FaLink /> Lien URL
                    </span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="photoMode"
                      value="upload"
                      checked={uploadMode === "upload"}
                      onChange={() => setUploadMode("upload")}
                    />
                    <span className="flex items-center gap-2 text-sm">
                      <FaCloudUploadAlt /> Uploader
                    </span>
                  </label>
                </div>

                {uploadMode === "url" ? (
                  <>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      URL de la photo
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        name="photoURL"
                        value={formData.photoURL}
                        onChange={handleChange}
                        placeholder="https://…/photo.jpg ou /uploads/photos_team/…"
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                        inputMode="url"
                      />
                    </div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      PNG/JPG/WebP recommandé. Taille carrée conseillée
                      (512×512).
                    </p>
                  </>
                ) : (
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white/70 dark:bg-slate-900/30">
                    {/* INPUT FILE CACHÉ */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className=" bg-primary rounded-xl gap-2 px-3 py-1.5 text-sm text-white hover:bg-primary-dark flex items-center hover:bg-white/90 hover:text-gray-800 transition ring-1 ring-primary/70 hover:ring-primary"
                          onClick={openFilePicker}
                        >
                          Choisir un fichier
                        </button>

                        <div className="flex items-center gap-2">
                          <span className="text-sm">Format</span>
                          <select
                            className="select select-bordered select-sm"
                            value={outputFormat}
                            onChange={(e) => setOutputFormat(e.target.value)}
                            disabled={isUploading}
                          >
                            <option value="webp">WEBP (recommandé)</option>
                            <option value="jpg">JPG</option>
                            <option value="png">PNG</option>
                          </select>
                        </div>
                      </div>

                      {isUploading && (
                        <div>
                          <progress
                            className="progress w-full"
                            value={uploadProgress}
                            max="100"
                          ></progress>
                          <div className="text-xs mt-1">{uploadProgress}%</div>
                        </div>
                      )}

                      <p className="text-xs opacity-70">
                        L’image sera traitée côté serveur en carré 400×400 et
                        optimisée.
                      </p>
                    </div>
                  </div>
                )}

                {/* Aperçu + Crop */}
                <div className="mt-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FaImage />
                    <span>Aperçu</span>
                  </div>

                  <div
                    className="mt-2 h-28 w-28 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
                    onClick={(e) => {
                      if (uploadMode === "upload") openFilePicker(e);
                    }}
                  >
                    {formData.photoURL ? (
                      <img
                        src={`${absolutePhoto}?v=${imgVersion}`}
                        alt="Aperçu"
                        onError={() => setImgOk(false)}
                        className="h-full w-full object-cover pointer-events-none"
                      />
                    ) : (
                      <span className="text-xs text-slate-500 px-2 text-center">
                        Aucun visuel
                      </span>
                    )}
                  </div>

                  {formData.photoURL && (
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs ring-1 ring-slate-200 dark:ring-slate-700 bg-slate-100 dark:bg-slate-800"
                      onClick={() => setIsCropOpen(true)}
                      title="Recadrer / Zoom"
                    >
                      <FaCrop /> Recadrer / Zoom
                    </button>
                  )}
                  {!imgOk && (
                    <div className="mt-2 text-xs text-rose-600">
                      Impossible de charger l’image.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Champs identité */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nom <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="DUPONT"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Prénom <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    placeholder="Alice"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Fonction <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaUserTie />
                  </span>
                  <input
                    type="text"
                    name="fonction"
                    value={formData.fonction}
                    onChange={handleChange}
                    required
                    placeholder="Enseignant, Trésorier, Président…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Catégorie <span className="text-pink-600">*</span>
                </label>
                <input
                  type="text"
                  name="categorie"
                  value={formData.categorie}
                  onChange={handleChange}
                  required
                  placeholder="Bureau, Enseignants, Bénévoles…"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Position (ordre) <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaListOl />
                  </span>
                  <input
                    type="number"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1"
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Email
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaEnvelope />
                  </span>
                  <input
                    type="email"
                    name="mail_member"
                    value={formData.mail_member}
                    onChange={handleChange}
                    placeholder="exemple@club.fr"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    inputMode="email"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Téléphone
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaPhone />
                  </span>
                  <input
                    type="tel"
                    name="tel_member"
                    value={formData.tel_member}
                    onChange={handleChange}
                    placeholder="+33 6 12 34 56 78"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Biographie
                </label>
                <div className="mt-1 rounded-xl border border-slate-300 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 quill-wrapper">
                  <ReactQuill
                    value={formData.bio_member}
                    onChange={(v) =>
                      setFormData({ ...formData, bio_member: v })
                    }
                    placeholder="Parcours, rôles dans le club, spécialités, disponibilités…"
                    modules={modules}
                    formats={formats}
                    className="min-h-[180px]"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Vous pouvez insérer du texte enrichi, des listes, des liens et
                  des images.
                </p>
              </div>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <Button
                text="Annuler"
                icon={FaTimes}
                onClick={handleClose}
                type="button"
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
              <Button
                text="Enregistrer"
                icon={FaSave}
                type="submit"
                className="!rounded-xl gap-2"
                disabled={isUploading}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Modale de recadrage Team */}
      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={memberData?.id}
        imageUrl={
          formData.photoURL
            ? `${toAbsTeamPhoto(
                toMasterUrl(formData.photoURL)
              )}?v=${imgVersion}`
            : ""
        }
        onCropped={(newUrl) => {
          // backend renvoie un chemin /uploads/...
          setFormData((p) => ({ ...p, photoURL: newUrl }));
          setImgVersion((v) => v + 1);
        }}
        format="webp"
        cropAction={cropTeamPhoto}
      />

      {/* Styles locaux */}
      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb { background: rgba(148,163,184,.35); }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
        .dark .quill-wrapper .ql-toolbar.ql-snow { background: rgba(15,23,42,.98); border-color: #334155; }
        .dark .quill-wrapper .ql-container.ql-snow { border-color: #334155; background: rgba(2,6,23,.7); }
        .dark .quill-wrapper .ql-editor { color: #e2e8f0; }
        .dark .ql-snow .ql-stroke { stroke: #e2e8f0 !important; }
        .dark .ql-snow .ql-fill { fill: #e2e8f0 !important; }
        .dark .ql-snow .ql-picker,
        .dark .ql-snow .ql-picker-label,
        .dark .ql-snow .ql-picker-options,
        .dark .ql-snow .ql-toolbar button { color: #e2e8f0 !important; }
        .dark .ql-snow .ql-picker-options { background: #0b1220; border-color: #334155; }
        .dark .ql-snow .ql-toolbar button:hover .ql-stroke { stroke: #fff !important; }
        .dark .ql-snow .ql-toolbar button:hover .ql-fill { fill: #fff !important; }
        .quill-wrapper .ql-editor.ql-blank::before { color: #64748b; opacity: .8; }
        .dark .quill-wrapper .ql-editor.ql-blank::before { color: #94a3b8; opacity: .7; }
      `}</style>
    </div>,
    document.body
  );
};

export default EditTeamMemberModal;
